import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

export default function VendorProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "clothing", subcategory: "",
    price: "", mrp: "", stock: "", sku: "", images: [""],
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor/products");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setImg = (i, v) => {
    const imgs = [...form.images];
    imgs[i] = v;
    setForm({ ...form, images: imgs });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        mrp: parseFloat(form.mrp),
        stock: parseInt(form.stock, 10),
        images: form.images.filter((u) => u.trim()),
      };
      await api.post("/vendor/products", payload);
      toast.success("Product submitted for review");
      setOpen(false);
      setForm({ title: "", description: "", category: "clothing", subcategory: "", price: "", mrp: "", stock: "", sku: "", images: [""] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add product");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/vendor/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Catalog</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E]">My Products</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white" data-testid="new-product-btn">
              <Plus size={16} className="mr-2" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">New Product</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="new-product-form">
              <div>
                <label className="text-xs text-[#3B5249]">Title</label>
                <Input required value={form.title} onChange={upd("title")} data-testid="p-title" />
              </div>
              <div>
                <label className="text-xs text-[#3B5249]">Description</label>
                <Textarea required value={form.description} onChange={upd("description")} rows={3} data-testid="p-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#3B5249]">Category</label>
                  <select className="mt-1 w-full h-10 rounded-md border border-[#E2E8E5] bg-white px-3 text-sm" value={form.category} onChange={upd("category")} data-testid="p-category">
                    <option value="clothing">Clothing</option>
                    <option value="electronics">Electronics</option>
                    <option value="grocery">Grocery</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#3B5249]">Subcategory (optional)</label>
                  <Input value={form.subcategory} onChange={upd("subcategory")} />
                </div>
                <div>
                  <label className="text-xs text-[#3B5249]">Price ₹</label>
                  <Input type="number" step="0.01" required value={form.price} onChange={upd("price")} data-testid="p-price" />
                </div>
                <div>
                  <label className="text-xs text-[#3B5249]">MRP ₹</label>
                  <Input type="number" step="0.01" required value={form.mrp} onChange={upd("mrp")} data-testid="p-mrp" />
                </div>
                <div>
                  <label className="text-xs text-[#3B5249]">Stock</label>
                  <Input type="number" required value={form.stock} onChange={upd("stock")} data-testid="p-stock" />
                </div>
                <div>
                  <label className="text-xs text-[#3B5249]">SKU (optional)</label>
                  <Input value={form.sku} onChange={upd("sku")} />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#3B5249]">Image URLs</label>
                {form.images.map((im, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <Input value={im} onChange={(e) => setImg(i, e.target.value)} placeholder="https://…" data-testid={`p-img-${i}`} />
                    {form.images.length > 1 && (
                      <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })} className="text-red-600"><X size={16}/></button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, images: [...form.images, ""] })} className="mt-2 text-xs text-[#14A085]">+ Add another image</button>
              </div>
              <Button type="submit" disabled={saving} className="w-full rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white" data-testid="p-submit">
                {saving ? "Submitting…" : "Submit for Review"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="mt-8 text-center text-[#3B5249]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-[#E2E8E5] bg-white p-12 text-center" data-testid="no-products">
          <p className="text-[#3B5249]">No products yet. Click "Add Product" to list your first item.</p>
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vendor-products-grid">
          {items.map((p) => (
            <div key={p.id} className="rounded-xl border border-[#E2E8E5] bg-white overflow-hidden" data-testid={`vp-${p.id}`}>
              <div className="aspect-video bg-[#F9F9F7] overflow-hidden">
                <img src={p.images?.[0] || "https://images.unsplash.com/photo-1561715276-a2d087060f1d"} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-1 rounded-full capitalize ${STATUS_COLORS[p.status] || "bg-gray-100"}`}>{p.status}</span>
                  <span className="text-xs text-[#6E877C]">{p.category}</span>
                </div>
                <div className="font-medium text-[#0F3D2E] line-clamp-1">{p.title}</div>
                <div className="mt-1 text-sm text-[#3B5249]">₹{p.price} · Stock: {p.stock}</div>
                <div className="mt-3 flex justify-end">
                  <button onClick={() => remove(p.id)} className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1" data-testid={`del-${p.id}`}>
                    <Trash2 size={14}/> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
